import { Box, Divider, FormHelperText, Typography } from "@mui/material"
import type { FC } from "react"

import {
  ACCOUNTS_PAYABLE_FILTERABLE_TRANSACTION_TYPES,
  ACCOUNTS_PAYABLE_TRANSACTION_TYPE_TO_INVOICE_STATUSES,
  ACCOUNTS_PAYABLE_TRANSACTION_TYPE_TO_LABEL,
  type AccountsPayableTransactionType,
} from "../../constants/accountsPayable"

import { SearchFilterDrawer } from "@/components/common/SearchFilterDrawer/SearchFilterDrawer"
import { AerosCheckbox, AerosDatePicker } from "@/components/UiKit"
import type { VendorApLedgerListFilter } from "@/graphql/codegen/graphql"

interface AccountsPayableTransactionFilterDrawerProps {
  filter: VendorApLedgerListFilter
  open: boolean
  onToggle: () => void
  onChange: (filter: VendorApLedgerListFilter) => void
  onClear: () => void
}

export const AccountsPayableTransactionFilterDrawer: FC<AccountsPayableTransactionFilterDrawerProps> = ({
  filter,
  open,
  onToggle,
  onChange,
  onClear,
}) => {
  const selectedInvoiceActions = filter.invoiceActions ?? []
  const { datePostedFrom, datePostedTo } = filter

  const isDateRangeInvalid = !!datePostedFrom && !!datePostedTo && datePostedTo < datePostedFrom

  const isTypeSelected = (type: AccountsPayableTransactionType) =>
    ACCOUNTS_PAYABLE_TRANSACTION_TYPE_TO_INVOICE_STATUSES[type].every((status) =>
      selectedInvoiceActions.includes(status)
    )

  const handleTypeChange = (type: AccountsPayableTransactionType) => {
    const statuses = ACCOUNTS_PAYABLE_TRANSACTION_TYPE_TO_INVOICE_STATUSES[type]
    const newInvoiceActions = isTypeSelected(type)
      ? selectedInvoiceActions.filter((selectedStatus) => !statuses.includes(selectedStatus))
      : [...selectedInvoiceActions, ...statuses.filter((status) => !selectedInvoiceActions.includes(status))]
    onChange({ ...filter, invoiceActions: newInvoiceActions })
  }

  const handleDateFromChange = (value: string | null) => {
    onChange({ ...filter, datePostedFrom: value ?? undefined })
  }

  const handleDateToChange = (value: string | null) => {
    onChange({ ...filter, datePostedTo: value ?? undefined })
  }

  return (
    <SearchFilterDrawer open={open} toggleDrawer={onToggle} clear={onClear} testId='accounts-payable.filter-drawer'>
      <Box className='h-full min-w-80'>
        <Box className='flex flex-1 flex-col'>
          <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
            Transaction Type
          </Typography>
          <Divider />
          <Box className='flex-1 overflow-y-auto overflow-x-hidden px-8'>
            {ACCOUNTS_PAYABLE_FILTERABLE_TRANSACTION_TYPES.map((type) => (
              <AerosCheckbox
                key={type}
                checked={isTypeSelected(type)}
                onChange={() => handleTypeChange(type)}
                label={ACCOUNTS_PAYABLE_TRANSACTION_TYPE_TO_LABEL[type]}
                className='w-full'
                data-testid={`accounts-payable.filter-drawer.type-checkbox-${type}`}
              />
            ))}
          </Box>
        </Box>

        <Box className='flex flex-1 flex-col'>
          <Typography variant='subtitle1' className='px-4 py-1.5 font-bold text-gray-700'>
            Date Posted
          </Typography>
          <Divider />
          <Box className='flex flex-col gap-4 px-8 py-4'>
            <AerosDatePicker
              label='From'
              value={datePostedFrom ?? null}
              onChange={handleDateFromChange}
              error={isDateRangeInvalid}
              data-testid='accounts-payable.filter-drawer.date-from-field'
            />
            <AerosDatePicker
              label='To'
              value={datePostedTo ?? null}
              onChange={handleDateToChange}
              error={isDateRangeInvalid}
              data-testid='accounts-payable.filter-drawer.date-to-field'
            />
            {isDateRangeInvalid && <FormHelperText error>From date must be before To date</FormHelperText>}
          </Box>
        </Box>
      </Box>
    </SearchFilterDrawer>
  )
}
